import React, { useState, useMemo } from 'react';
import { ArrowLeft, Search, SlidersHorizontal, Upload, ChevronRight } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { STATUS_CONFIG, MONTHLY_DATA, formatINR } from '@/data/mockData';

const STATUS_OPTS = ['All', 'Pending Upload', 'OCR Needs Confirmation', 'Under Review', 'Verified'];
const MONTH_OPTS = ['All Months', 'Mar 2026', 'Feb 2026', 'Jan 2026', 'Dec 2025', 'Nov 2025', 'Oct 2025'];

const statusLabelToKey = {
  'Pending Upload': 'PENDING_UPLOAD',
  'OCR Needs Confirmation': 'OCR_CONFIRMATION',
  'Under Review': 'UNDER_REVIEW',
  'Verified': 'VERIFIED',
};

export default function PendingInvoicesScreen() {
  const { goBack, navigate, invoices, screenParams, pendingValue, pendingCount } = useApp();
  const [searchQ, setSearchQ] = useState('');
  const [selMonth, setSelMonth] = useState(screenParams.filterMonth || 'All Months');
  const [selStatus, setSelStatus] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      const matchSearch = searchQ === '' ||
        inv.invoiceNo.toLowerCase().includes(searchQ.toLowerCase()) ||
        inv.buyer.toLowerCase().includes(searchQ.toLowerCase());
      const matchMonth = selMonth === 'All Months' || inv.month === selMonth;
      const matchStatus = selStatus === 'All' ||
        inv.status === statusLabelToKey[selStatus] ||
        STATUS_CONFIG[inv.status]?.label === selStatus;
      return matchSearch && matchMonth && matchStatus;
    });
  }, [invoices, searchQ, selMonth, selStatus]);

  const totalFiltered = filtered.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-5">
        <div className="flex items-center gap-3 mb-4">
          <button
            data-testid="pending-back-btn"
            onClick={goBack}
            className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center tap-scale"
          >
            <ArrowLeft size={18} color="white" />
          </button>
          <h1 className="text-white font-bold text-lg">Pending Invoices</h1>
        </div>
        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-3 text-center">
            <div className="text-white font-black text-lg leading-none">{pendingCount}</div>
            <div className="text-white/70 text-[10px] font-semibold mt-0.5">Pending</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3 text-center">
            <div className="text-white font-black text-sm leading-none">₹{formatINR(pendingValue)}</div>
            <div className="text-white/70 text-[10px] font-semibold mt-0.5">Value</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3 text-center">
            <div className="text-white font-black text-lg leading-none">Mar 26</div>
            <div className="text-white/70 text-[10px] font-semibold mt-0.5">Month</div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        {/* Search */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm">
            <Search size={16} color="#94A3B8" />
            <input
              data-testid="invoice-search-input"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search invoice or buyer..."
              className="flex-1 text-sm outline-none bg-transparent text-slate-700 placeholder-slate-400"
            />
          </div>
          <button
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
            className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-sm tap-scale border ${showFilters ? 'bg-[#2563EB] border-[#2563EB]' : 'bg-white border-slate-200'}`}
          >
            <SlidersHorizontal size={16} color={showFilters ? 'white' : '#64748B'} />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl p-3 mb-3 border border-slate-100 shadow-sm slide-up">
            <div className="mb-2">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Month</div>
              <div className="flex flex-wrap gap-1.5">
                {MONTH_OPTS.map(m => (
                  <button
                    key={m}
                    data-testid={`filter-month-${m.replace(' ', '-')}`}
                    onClick={() => setSelMonth(m)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold tap-scale ${selMonth === m ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Status</div>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTS.map(s => (
                  <button
                    key={s}
                    data-testid={`filter-status-${s.replace(/\s+/g, '-')}`}
                    onClick={() => setSelStatus(s)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold tap-scale ${selStatus === s ? 'bg-[#FF7043] text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results count */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-slate-500 font-medium">{filtered.length} invoice(s) found</span>
          <span className="text-xs font-bold text-[#1E2744]">₹{formatINR(totalFiltered)}</span>
        </div>

        {/* Invoice list */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <div className="text-4xl mb-2">📋</div>
            <div className="font-semibold">No invoices found</div>
            <div className="text-sm mt-1">Try changing your filters</div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map(inv => {
              const cfg = STATUS_CONFIG[inv.status];
              const isAction = ['PENDING_UPLOAD', 'OCR_CONFIRMATION'].includes(inv.status);
              return (
                <button
                  key={inv.id}
                  data-testid={`invoice-row-${inv.id}`}
                  onClick={() => navigate('UPLOAD_INVOICE', { invoice: inv })}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left w-full tap-scale"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-[#1E2744] text-sm">{inv.invoiceNo}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{inv.date}</div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${cfg.className}`}>
                      {cfg.label}
                    </span>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-xs text-slate-400 font-medium">Buyer Name</div>
                      <div className="text-sm font-semibold text-slate-700">{inv.buyer}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Sale Value</div>
                        <div className="rupee-value font-bold text-[#1E2744]">₹{formatINR(inv.amount)}</div>
                      </div>
                      {isAction && (
                        <div className="w-8 h-8 bg-[#FF7043] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Upload size={14} color="white" />
                        </div>
                      )}
                      {!isAction && (
                        <ChevronRight size={16} color="#94A3B8" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
